import { useState, useRef, useEffect, useCallback } from 'react'
import { X, Minus, Plus, RotateCcw, ImageIcon, Target } from 'lucide-react'
import { flowNodes, ownerStyles } from '../data'
import type { FlowNode, SubProcess } from '../data'

// ── Offset-based rect (unaffected by CSS transforms) ────────────────────────

function getOffsetRect(el: HTMLElement | null | undefined, container: HTMLElement | null) {
  if (!el || !container) return null
  let left = 0, top = 0
  let current: HTMLElement | null = el
  while (current && current !== container) {
    left += current.offsetLeft
    top += current.offsetTop
    current = current.offsetParent as HTMLElement | null
  }
  return { l: left, t: top, r: left + el.offsetWidth, b: top + el.offsetHeight, cx: left + el.offsetWidth / 2, cy: top + el.offsetHeight / 2 }
}

// ── SVG arrows ──────────────────────────────────────────────────────────────

function HArrow({ from, to, container }: { from?: HTMLElement | null; to?: HTMLElement | null; container: HTMLElement | null }) {
  const a = getOffsetRect(from, container)
  const b = getOffsetRect(to, container)
  if (!a || !b) return null
  const x1 = a.r, y1 = a.cy, x2 = b.l, y2 = b.cy, mx = (x1 + x2) / 2
  return (
    <g>
      <path d={`M ${x1} ${y1} C ${mx} ${y1}, ${mx} ${y2}, ${x2} ${y2}`} fill="none" stroke="var(--border-color)" strokeWidth={2} />
      <polygon points={`${x2},${y2} ${x2 - 7},${y2 - 3.5} ${x2 - 7},${y2 + 3.5}`} fill="var(--border-color)" />
    </g>
  )
}

function VArrow({ from, to, container }: { from?: HTMLElement | null; to?: HTMLElement | null; container: HTMLElement | null }) {
  const a = getOffsetRect(from, container)
  const b = getOffsetRect(to, container)
  if (!a || !b) return null
  const x1 = a.cx, y1 = a.b, x2 = b.cx, y2 = b.t, my = (y1 + y2) / 2
  return (
    <g>
      <path d={`M ${x1} ${y1} C ${x1} ${my}, ${x2} ${my}, ${x2} ${y2}`} fill="none" stroke="var(--border-color)" strokeWidth={1.5} strokeDasharray="6 3" />
      <polygon points={`${x2},${y2} ${x2 - 3.5},${y2 - 7} ${x2 + 3.5},${y2 - 7}`} fill="var(--border-color)" />
    </g>
  )
}

// ── Sidebar panel (inside the canvas, right side) ───────────────────────────

function SidebarPanel({ node, sub, onClose }: { node?: FlowNode | null; sub?: SubProcess | null; onClose: () => void }) {
  const isNode = !!node
  const title = node?.title ?? sub?.title ?? ''
  const style = node ? ownerStyles[node.owner] : null
  const [activeImageIdx, setActiveImageIdx] = useState(0)
  const [expandedImage, setExpandedImage] = useState<string | null>(null)

  // Determine the list of images (from sub-process or main node)
  const source = sub ?? node
  const imageList = source?.images ?? (source?.image ? [source.image] : [])
  const sourceKey = node?.id ?? sub?.title ?? ''

  // Reset image index when switching between items
  useEffect(() => { setActiveImageIdx(0); setExpandedImage(null) }, [sourceKey])

  return (
    <div
      className="absolute top-0 right-0 h-full w-[520px] max-w-[55%] z-20 border-l border-[var(--border-color)] bg-[var(--bg-primary)] overflow-y-auto shadow-2xl"
      onWheel={(e) => e.stopPropagation()}
    >
      {/* Header */}
      <div className="sticky top-0 z-10 flex items-start justify-between gap-3 p-5 pb-4 bg-[var(--bg-primary)] border-b border-[var(--border-color)]">
        <div className="min-w-0">
          <span className={`text-[10px] font-bold uppercase tracking-widest ${isNode && style ? style.text : 'text-purple-400'}`}>
            {isNode && style ? style.label : 'Presales Activity'}
          </span>
          <h3 className="text-base font-bold text-[var(--text-primary)] mt-0.5 leading-tight">{title}</h3>
          {node?.subtitle && <p className="text-xs text-[var(--text-muted)] mt-0.5">{node.subtitle}</p>}
        </div>
        <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-[var(--bg-tertiary)] text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-colors shrink-0 mt-0.5">
          <X size={16} />
        </button>
      </div>

      <div className="p-5 space-y-5">
        {/* Image gallery (sub-processes) */}
        {imageList.length > 0 && (
          <div className="space-y-2">
            <div
              className="rounded-xl border border-[var(--border-color)] bg-[var(--bg-secondary)] overflow-hidden cursor-pointer hover:border-[var(--accent-color)] transition-colors"
              onClick={() => setExpandedImage(imageList[activeImageIdx])}
              title="Click to enlarge"
            >
              <img
                src={imageList[activeImageIdx]}
                alt={`${sub?.title ?? ''} screenshot ${activeImageIdx + 1}`}
                className="w-full h-auto"
                onError={(e) => {
                  const target = e.currentTarget
                  target.style.display = 'none'
                  const placeholder = target.nextElementSibling as HTMLElement
                  if (placeholder) placeholder.style.display = 'flex'
                }}
              />
              <div className="hidden flex-col items-center justify-center py-10 text-[var(--text-muted)]">
                <ImageIcon size={32} className="mb-2 opacity-40" />
                <span className="text-xs">Image placeholder</span>
                <span className="text-[10px] mt-0.5 opacity-60">{imageList[activeImageIdx]}</span>
              </div>
            </div>
            {imageList.length > 1 && (
              <div className="flex items-center justify-center gap-2">
                {imageList.map((_, idx) => (
                  <button
                    key={idx}
                    onClick={() => setActiveImageIdx(idx)}
                    className={`w-2 h-2 rounded-full transition-all ${
                      idx === activeImageIdx ? 'bg-purple-400 w-5' : 'bg-[var(--border-color)] hover:bg-[var(--text-muted)]'
                    }`}
                  />
                ))}
                <span className="text-[10px] text-[var(--text-muted)] ml-2">{activeImageIdx + 1} / {imageList.length}</span>
              </div>
            )}
            <p className="text-[10px] text-[var(--text-muted)] text-center">Click image to enlarge</p>
          </div>
        )}

        {/* Expanded image overlay */}
        {expandedImage && (
          <div
            className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-8 cursor-pointer"
            onClick={() => setExpandedImage(null)}
          >
            <div className="relative max-w-[90vw] max-h-[90vh]">
              <img src={expandedImage} alt="Enlarged view" className="max-w-full max-h-[85vh] rounded-xl shadow-2xl" />
              <button
                onClick={() => setExpandedImage(null)}
                className="absolute -top-3 -right-3 p-2 rounded-full bg-[var(--bg-secondary)] border border-[var(--border-color)] text-[var(--text-muted)] hover:text-[var(--text-primary)] shadow-lg"
              >
                <X size={16} />
              </button>
              {imageList.length > 1 && (
                <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex items-center gap-3 bg-black/60 rounded-full px-4 py-2">
                  {imageList.map((img, idx) => (
                    <button
                      key={idx}
                      onClick={(e) => { e.stopPropagation(); setActiveImageIdx(idx); setExpandedImage(img) }}
                      className={`w-2.5 h-2.5 rounded-full transition-all ${
                        img === expandedImage ? 'bg-white w-6' : 'bg-white/40 hover:bg-white/70'
                      }`}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Content for main nodes */}
        {isNode && node && (
          <>
            {node.metric && (
              <div className="flex items-start gap-2.5 p-3 rounded-lg bg-amber-500/10 border border-amber-500/20">
                <Target size={14} className="text-amber-400 shrink-0 mt-0.5" />
                <div>
                  <p className="text-[10px] font-bold uppercase tracking-widest text-amber-400 mb-0.5">Metric</p>
                  <p className="text-xs text-[var(--text-secondary)]">{node.metric}</p>
                </div>
              </div>
            )}
            <div>
              <p className="text-[10px] font-bold uppercase tracking-widest text-[var(--text-muted)] mb-2">What happens at this stage</p>
              <ul className="space-y-1.5">
                {node.whatWeDo.map((item, i) => (
                  <li key={i} className="flex gap-2.5 text-sm text-[var(--text-secondary)]">
                    <span className={`w-1.5 h-1.5 rounded-full ${style!.dot} shrink-0 mt-1.5`} />{item}
                  </li>
                ))}
              </ul>
            </div>
            {node.howItWorks && (
              <div className="pt-4 border-t border-[var(--border-color)]">
                <p className="text-[10px] font-bold uppercase tracking-widest text-[var(--text-muted)] mb-2">How it works</p>
                <ul className="space-y-1.5">
                  {node.howItWorks.map((item, i) => (
                    <li key={i} className="flex gap-2.5 text-sm text-[var(--text-muted)]">
                      <span className="w-1.5 h-1.5 rounded-full bg-slate-400 shrink-0 mt-1.5" />{item}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {node.branch && (
              <div className="pt-4 border-t border-[var(--border-color)]">
                <p className="text-[10px] font-bold uppercase tracking-widest text-[var(--text-muted)] mb-2">Parallel track</p>
                <p className="text-sm font-medium text-[var(--text-primary)] mb-2">{node.branch.title}</p>
                <ul className="space-y-1.5">
                  {node.branch.details.map((d, i) => (
                    <li key={i} className="flex gap-2.5 text-sm text-[var(--text-muted)]">
                      <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 shrink-0 mt-1.5" />{d}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </>
        )}

        {/* Content for sub-processes */}
        {sub && (
          <>
            {sub.metric && (
              <div className="flex items-start gap-2.5 p-3 rounded-lg bg-amber-500/10 border border-amber-500/20">
                <Target size={14} className="text-amber-400 shrink-0 mt-0.5" />
                <div>
                  <p className="text-[10px] font-bold uppercase tracking-widest text-amber-400 mb-0.5">Metric</p>
                  <p className="text-xs text-[var(--text-secondary)]">{sub.metric}</p>
                </div>
              </div>
            )}
            <div>
              <p className="text-[10px] font-bold uppercase tracking-widest text-[var(--text-muted)] mb-2">What Presales does</p>
              <ul className="space-y-1.5">
                {sub.details.map((d, i) => (
                  <li key={i} className="flex gap-2.5 text-sm text-[var(--text-secondary)]">
                    <span className="w-1.5 h-1.5 rounded-full bg-purple-400 shrink-0 mt-1.5" />{d}
                  </li>
                ))}
              </ul>
            </div>
            {sub.whyItMatters && (
              <div className="pt-4 border-t border-[var(--border-color)]">
                <p className="text-[10px] font-bold uppercase tracking-widest text-[var(--text-muted)] mb-2">Why it matters</p>
                <div className="flex flex-wrap gap-2">
                  {sub.whyItMatters.map((reason, i) => (
                    <span key={i} className="text-xs px-2.5 py-1 rounded-lg bg-purple-500/10 text-purple-400">{reason}</span>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  )
}

// ── Main flowchart ──────────────────────────────────────────────────────────

export function FlowchartView() {
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null)
  const [selectedSubTitle, setSelectedSubTitle] = useState<string | null>(null)
  const [zoom, setZoomRaw] = useState(0.85)
  const [panX, setPanX] = useState(40)
  const [panY, setPanY] = useState(40)
  const [isPanning, setIsPanning] = useState(false)
  const [tick, setTick] = useState(0)

  const canvasRef = useRef<HTMLDivElement>(null)
  const contentRef = useRef<HTMLDivElement>(null)
  const elMap = useRef<Record<string, HTMLElement | null>>({})
  const panStart = useRef({ x: 0, y: 0, panX: 0, panY: 0 })

  const activitiesNode = flowNodes.find((n) => n.subProcesses && n.subProcesses.length > 0)!
  const selectedNode = flowNodes.find((n) => n.id === selectedNodeId) ?? null
  const selectedSub = activitiesNode.subProcesses?.find((s) => s.title === selectedSubTitle) ?? null
  const branchParents = flowNodes.filter((n) => n.branch)
  const sidebarOpen = !!(selectedNode || selectedSub)

  const setZoom = useCallback((v: number) => setZoomRaw(Math.max(0.3, Math.min(2, v))), [])
  const setPan = useCallback((x: number, y: number) => { setPanX(x); setPanY(y) }, [])

  useEffect(() => {
    const t = setTimeout(() => setTick((v) => v + 1), 150)
    return () => clearTimeout(t)
  }, [])

  const handleWheel = useCallback((e: React.WheelEvent) => {
    if (e.ctrlKey || e.metaKey) {
      e.preventDefault()
      setZoom(zoom + (e.deltaY > 0 ? -0.1 : 0.1))
    } else {
      setPan(panX - e.deltaX, panY - e.deltaY)
    }
  }, [zoom, panX, panY, setZoom, setPan])

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    const target = e.target as HTMLElement
    if (target.closest('[data-node]') || target.closest('button') || target.closest('[data-sidebar]')) return
    if (e.button === 0 || e.button === 1) {
      setIsPanning(true)
      panStart.current = { x: e.clientX, y: e.clientY, panX, panY }
      const handleMouseMove = (ev: MouseEvent) => {
        setPan(panStart.current.panX + ev.clientX - panStart.current.x, panStart.current.panY + ev.clientY - panStart.current.y)
      }
      const handleMouseUp = () => {
        setIsPanning(false)
        window.removeEventListener('mousemove', handleMouseMove)
        window.removeEventListener('mouseup', handleMouseUp)
      }
      window.addEventListener('mousemove', handleMouseMove)
      window.addEventListener('mouseup', handleMouseUp)
    }
  }, [panX, panY, setPan])

  const setRef = useCallback((key: string) => (el: HTMLDivElement | null) => { elMap.current[key] = el }, [])

  const selectNode = (id: string) => {
    setSelectedSubTitle(null)
    setSelectedNodeId(selectedNodeId === id ? null : id)
  }
  const selectSub = (title: string) => {
    setSelectedNodeId(null)
    setSelectedSubTitle(selectedSubTitle === title ? null : title)
  }
  const closeAll = () => { setSelectedNodeId(null); setSelectedSubTitle(null) }

  void tick

  return (
    <div className="space-y-4">
      {/* Legend */}
      <div className="flex flex-wrap items-center gap-4">
        {Object.entries(ownerStyles).map(([key, style]) => (
          <div key={key} className="flex items-center gap-1.5">
            <div className={`w-3 h-3 rounded-full ${style.dot}`} />
            <span className="text-xs font-medium text-[var(--text-muted)]">{style.label}</span>
          </div>
        ))}
        <span className="text-xs text-[var(--text-muted)] ml-auto">Scroll to pan / Ctrl+scroll to zoom / Drag to pan</span>
      </div>

      {/* Canvas */}
      <div
        ref={canvasRef}
        onWheel={handleWheel}
        onMouseDown={handleMouseDown}
        className="relative w-full overflow-hidden rounded-xl border border-[var(--border-color)]"
        style={{
          height: 560,
          cursor: isPanning ? 'grabbing' : 'grab',
          backgroundImage: 'radial-gradient(circle, var(--border-color) 1px, transparent 1px)',
          backgroundSize: `${20 * zoom}px ${20 * zoom}px`,
          backgroundPosition: `${panX}px ${panY}px`,
        }}
      >
        {/* Transformed content layer */}
        <div
          ref={contentRef}
          className="absolute inset-0"
          style={{
            transform: `translate(${panX}px, ${panY}px) scale(${zoom})`,
            transformOrigin: '0 0',
          }}
        >
          {/* SVG arrows */}
          <svg className="absolute inset-0 pointer-events-none" style={{ width: 2000, height: 800, overflow: 'visible' }}>
            {flowNodes.map((node, i) => {
              if (i === flowNodes.length - 1) return null
              return <HArrow key={`h-${node.id}`} from={elMap.current[node.id]} to={elMap.current[flowNodes[i + 1].id]} container={contentRef.current} />
            })}
            {branchParents.map((bp) => (
              <VArrow key={`vb-${bp.id}`} from={elMap.current[bp.id]} to={elMap.current[`__branch_${bp.id}`]} container={contentRef.current} />
            ))}
          </svg>

          {/* Main flow row */}
          <div className="flex items-center gap-10 pt-8 pl-8" style={{ width: 'max-content' }}>
            {flowNodes.map((node) => {
              const style = ownerStyles[node.owner]
              const isGate = node.owner === 'gate'
              const isSelected = selectedNodeId === node.id
              const hasSubProcesses = node.subProcesses && node.subProcesses.length > 0

              if (hasSubProcesses) {
                return (
                  <div
                    key={node.id}
                    ref={setRef(node.id)}
                    data-node
                    className={`
                      relative shrink-0 flex flex-col items-center text-center
                      px-5 pt-4 pb-3 min-w-[220px] max-w-[280px]
                      rounded-2xl border-2 transition-all duration-200
                      ${isSelected ? `${style.border} ${style.bg} shadow-lg` : `${style.border} ${style.bg}`}
                    `}
                  >
                    <span className={`text-[9px] font-bold uppercase tracking-widest ${style.text} mb-1`}>{style.label}</span>
                    <h3
                      className="text-[13px] font-bold text-[var(--text-primary)] leading-tight cursor-pointer hover:text-[var(--accent-color)] transition-colors"
                      onClick={() => selectNode(node.id)}
                    >
                      {node.title}
                    </h3>
                    {node.subtitle && <p className="text-[10px] text-[var(--text-muted)] mt-0.5">{node.subtitle}</p>}
                    <div className="flex flex-wrap justify-center gap-1.5 mt-3 w-full">
                      {node.subProcesses!.map((sub) => {
                        const isSubSelected = selectedSubTitle === sub.title
                        return (
                          <button
                            key={sub.title}
                            onClick={(e) => { e.stopPropagation(); selectSub(sub.title) }}
                            className={`text-[10px] font-medium px-2 py-1 rounded-md transition-all ${
                              isSubSelected
                                ? 'bg-purple-400 text-white shadow-sm'
                                : 'bg-purple-500/10 text-purple-300 hover:bg-purple-500/20 hover:text-purple-200'
                            }`}
                          >
                            {sub.title}
                          </button>
                        )
                      })}
                    </div>
                  </div>
                )
              }

              return (
                <div
                  key={node.id}
                  ref={setRef(node.id)}
                  data-node
                  onClick={() => selectNode(node.id)}
                  className={`
                    relative cursor-pointer select-none shrink-0
                    flex flex-col items-center justify-center text-center
                    px-5 py-4 min-w-[150px] max-w-[180px]
                    rounded-2xl border-2 transition-all duration-200
                    ${isGate ? 'border-dashed' : ''}
                    ${isSelected
                      ? `${style.border} ${style.bg} scale-105 shadow-lg`
                      : `${style.border} ${style.bg} hover:scale-[1.03] hover:shadow-md`
                    }
                  `}
                >
                  <span className={`text-[9px] font-bold uppercase tracking-widest ${style.text} mb-1.5`}>{style.label}</span>
                  <h3 className="text-[13px] font-bold text-[var(--text-primary)] leading-tight">{node.title}</h3>
                  {node.subtitle && <p className="text-[10px] text-[var(--text-muted)] mt-1 leading-snug">{node.subtitle}</p>}
                </div>
              )
            })}
          </div>

          {/* Branch nodes (positioned below their parents) */}
          {branchParents.map((bp) => {
            if (!bp.branch) return null
            const parentEl = elMap.current[bp.id]
            const cEl = contentRef.current
            let leftPos = 140
            if (parentEl && cEl) {
              const rect = getOffsetRect(parentEl, cEl)
              if (rect) leftPos = rect.cx - 80
            }
            const branchStyle = bp.owner === 'gate'
              ? { border: 'border-blue-500/30', bg: 'bg-blue-500/5', hoverBorder: 'hover:border-blue-400/60', textColor: 'text-blue-400', label: 'CS + Tech Ops' }
              : { border: 'border-emerald-500/30', bg: 'bg-emerald-500/5', hoverBorder: 'hover:border-emerald-400/60', textColor: 'text-emerald-400', label: 'Implementations' }
            return (
              <div
                key={`branch-${bp.id}`}
                ref={setRef(`__branch_${bp.id}`)}
                data-node
                onClick={() => selectNode(bp.id)}
                className={`absolute cursor-pointer select-none flex flex-col items-center justify-center text-center px-4 py-3 min-w-[160px] max-w-[190px] rounded-xl border-2 border-dashed ${branchStyle.border} ${branchStyle.bg} ${branchStyle.hoverBorder} hover:scale-[1.03] transition-all`}
                style={{ left: leftPos, top: 240 }}
              >
                <span className={`text-[9px] font-bold uppercase tracking-widest ${branchStyle.textColor} mb-1`}>{branchStyle.label}</span>
                <span className="text-[11px] font-medium text-[var(--text-secondary)] leading-tight">{bp.branch.title}</span>
              </div>
            )
          })}
        </div>

        {/* Sidebar panel (overlays right half of canvas) */}
        {sidebarOpen && (
          <div data-sidebar>
            <SidebarPanel node={selectedNode} sub={selectedSub} onClose={closeAll} />
          </div>
        )}

        {/* Zoom controls */}
        <div className="absolute bottom-3 left-3 flex items-center gap-1 bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-lg p-1 z-10">
          <button onClick={() => setZoom(zoom - 0.1)} className="p-1.5 rounded hover:bg-[var(--bg-tertiary)] text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-colors">
            <Minus size={14} />
          </button>
          <span className="text-[11px] font-mono text-[var(--text-muted)] w-10 text-center">{Math.round(zoom * 100)}%</span>
          <button onClick={() => setZoom(zoom + 0.1)} className="p-1.5 rounded hover:bg-[var(--bg-tertiary)] text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-colors">
            <Plus size={14} />
          </button>
          <div className="w-px h-4 bg-[var(--border-color)] mx-0.5" />
          <button onClick={() => { setZoom(0.85); setPan(40, 40) }} className="p-1.5 rounded hover:bg-[var(--bg-tertiary)] text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-colors">
            <RotateCcw size={14} />
          </button>
        </div>
      </div>
    </div>
  )
}
